import type { ChangeRequest, ChangeStatus } from '../types';

/** Format ISO date string → DD/MM/YYYY */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB');
}

/** Format ISO date string → DD/MM/YYYY, HH:MM */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB');
}

/** Safely parse JSON with a fallback */
export function safeJson<T>(str: string | null | undefined, fallback: T): T {
  if (!str) return fallback;
  try { return JSON.parse(str) as T; } catch { return fallback; }
}

/** Returns CSS badge modifier class for a change status */
export function statusBadgeClass(status: ChangeStatus | string | null | undefined): string {
  if (!status) return 'badge--muted';
  const s = status.toLowerCase();
  if (s === 'closed') return 'badge--accent';
  if (s === 'rejected' || s === 'rejection review') return 'badge--danger';
  if (s === 'ready for approval' || s === 'walkthrough requested') return 'badge--warn';
  if (s.includes('awaiting') || s.includes('notification')) return 'badge--secondary';
  return 'badge--primary';
}

/** Returns CSS badge modifier class for stakeholder response/sign-off status */
export function responseBadgeClass(status: string | null | undefined): string {
  if (!status) return 'badge--muted';
  const map: Record<string, string> = {
    'Responded':     'badge--accent',
    'Pending':       'badge--warn',
    'No Response':   'badge--muted',
    'Signed Off':    'badge--accent',
    'Concern Raised':'badge--danger',
    'Not Required':  'badge--muted',
  };
  return map[status] ?? 'badge--primary';
}

/** Returns CSS badge modifier class for survey status */
export function surveyBadgeClass(status: string | null | undefined): string {
  if (!status) return 'badge--muted';
  if (status === 'Successful')   return 'badge--accent';
  if (status === 'Partial')      return 'badge--warn';
  if (status === 'Unsuccessful') return 'badge--danger';
  return 'badge--muted';
}

/** Stage name → step indicator index (0-based). Legacy 'Step N' keys kept for existing DB records. */
export const stepIndexMap: Record<string, number> = {
  'Initiate': 0,
  'Notify & Respond': 1,
  'Plan': 2,
  'Approve': 3,
  'Implement': 4,
  'Review': 5,
  'Closed': -1,
  // Legacy values — existing records in DB
  'Step 1': 0, 'Step 2 Pending': 0,
  'Step 3': 1,
  'Step 4 Pending': 2, 'Step 4': 2,
  'Step 5 Pending': 3, 'Step 5': 3,
  'Step 6': 4,
  'Step 7': 5, 'Step 7 — Closure': 5,
};

/** Status → active section ID map for ManageChange */
export const sectionMap: Record<string, string> = {
  'Submitted':                         'notify',
  'Awaiting Stakeholder Notification': 'notify',
  'Stakeholder Review':                'responses',
  'Planning':                          'plan',
  'Plan Submitted':                    'signoff',
  'Pending Sign-Off':                  'signoff',
  'Ready for Approval':                'approval',
  'Walkthrough Requested':             'approval',
  'Approved':                          'implementation',
  'In Implementation':                 'implementation',
  'Implementation Complete':           'implementation',
  'Under Review':                      'review',
  'Pending Sponsor Closure':           'review',
  'Rejected':                          'rejection',
  'Rejection Review':                  'rejection',
  'Closed':                            'closed',
};

/** Action label + URL for a change record (used on Home page table) */
export function getActionConfig(rec: ChangeRequest): { label: string; url: string } {
  const base   = `/manage-change?cid=${encodeURIComponent(rec.id)}`;
  const detail = `/change-detail?cid=${encodeURIComponent(rec.id)}`;
  const map: Partial<Record<ChangeStatus, { label: string; url: string }>> = {
    'Awaiting Stakeholder Notification': { label: 'Notify Stakeholders',  url: base   },
    'Submitted':                         { label: 'Notify Stakeholders',  url: base   },
    'Stakeholder Review':                { label: 'View Responses',       url: base   },
    'Planning':                          { label: 'Submit Plan',          url: base   },
    'Plan Submitted':                    { label: 'Sign-Off Dashboard',   url: base   },
    'Pending Sign-Off':                  { label: 'Sign-Off Dashboard',   url: base   },
    'Ready for Approval':                { label: 'Submit for Approval',  url: base   },
    'Walkthrough Requested':             { label: 'Submit for Approval',  url: base   },
    'Rejected':                          { label: 'Review Rejection',     url: base   },
    'Rejection Review':                  { label: 'Review Rejection',     url: base   },
    'Approved':                          { label: 'Implementation',       url: base   },
    'In Implementation':                 { label: 'Implementation',       url: base   },
    'Implementation Complete':           { label: 'Implementation',       url: base   },
    'Under Review':                      { label: 'Post-Impl Review',     url: base   },
    'Pending Sponsor Closure':           { label: 'Post-Impl Review',     url: base   },
    'Closed':                            { label: 'View Record',          url: detail },
  };
  return map[rec.status as ChangeStatus] ?? { label: 'Manage', url: base };
}

/** Overdue check */
export function isOverdue(rec: ChangeRequest): boolean {
  if (['Closed', 'Rejected'].includes(rec.status)) return false;
  const submitted = rec.dateSubmitted ? new Date(rec.dateSubmitted) : null;
  const modified  = rec.lastModified  ? new Date(rec.lastModified)  : null;
  const now = new Date();
  const daysSinceSubmit   = submitted ? (now.getTime() - submitted.getTime()) / 86400000 : 0;
  const daysSinceModified = modified  ? (now.getTime() - modified.getTime())  / 86400000 : 0;
  return daysSinceSubmit > 30 && daysSinceModified > 7;
}

/** Is this date in the current month? */
export function isThisMonth(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

/** Encode a file to base64 (strip data URI prefix) */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = () => reject(new Error('File read failed'));
    reader.readAsDataURL(file);
  });
}

/**
 * Statuses in which a change's submission can still be amended, and in which its plan can still be drafted.
 * Mirrors server/src/services/amendments.ts. The server is the authority; this only decides whether to SHOW the button.
 */
export const AMENDABLE_STATUSES = [
  'Submitted', 'Awaiting Stakeholder Notification', 'Stakeholder Review', 'Planning', 'Plan Submitted', 'Pending Sign-Off',
  'Ready for Approval', 'Walkthrough Requested', 'Rejected', 'Rejection Review',
];
export const PLAN_DRAFT_STATUSES = ['Submitted', 'Awaiting Stakeholder Notification', 'Stakeholder Review', 'Planning'];
/** Statuses where approvers or stakeholders have already been asked to decide, so an amendment changes what they are judging. */
export const POST_SIGNOFF_STATUSES = ['Plan Submitted', 'Pending Sign-Off', 'Ready for Approval', 'Walkthrough Requested'];
